import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  TablePagination,
  TextField,
  MenuItem,
  Grid,
  LinearProgress,
  Alert,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material'
import {
  Visibility as ViewIcon,
  PlayArrow as ApplyIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  ViewList as ViewListIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material'
import { useUpdates, useApplyUpdate, useDeleteUpdate, useBulkDeleteUpdates, useBulkApplyUpdates } from '@/hooks/useApi'
import { DataUpdate, UpdateStatus } from '@/types'
import { format } from 'date-fns'
import { fr } from 'date-fns/locale'

const UpdateList: React.FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [statusFilter, setStatusFilter] = useState<UpdateStatus | ''>("")
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')

  // ✅ Debounce de 300ms sur la recherche
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchQuery])
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [viewMode, setViewMode] = useState<'grouped' | 'table'>('grouped')  // ✅ Nouveau
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())  // ✅ Nouveau
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean
    action: 'delete' | 'apply' | null
    count: number
  }>({ open: false, action: null, count: 0 })

  const { data: updatesData, isLoading, error } = useUpdates(
    {
      status: statusFilter || undefined,
      search: debouncedSearchQuery || undefined,
    },
    rowsPerPage,
    page * rowsPerPage
  )

  const applyUpdateMutation = useApplyUpdate()
  const deleteUpdateMutation = useDeleteUpdate()
  const bulkDeleteMutation = useBulkDeleteUpdates()
  const bulkApplyMutation = useBulkApplyUpdates()

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleApplyUpdate = async (updateId: string) => {
    try {
      await applyUpdateMutation.mutateAsync(updateId)
    } catch (error) {
      console.error('Error applying update:', error)
    }
  }

  const handleDeleteUpdate = async (updateId: string) => {
    try {
      await deleteUpdateMutation.mutateAsync(updateId)
    } catch (error) {
      console.error('Error deleting update:', error)
    }
  }

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allIds = updates.map((update: DataUpdate) => update.id)
      setSelectedIds(allIds)
    } else {
      setSelectedIds([])
    }
  }

  const handleSelectOne = (id: string) => {
    setSelectedIds(prev =>
      prev.includes(id)
        ? prev.filter(selectedId => selectedId !== id)
        : [...prev, id]
    )
  }

  const handleBulkDelete = () => {
    setConfirmDialog({ open: true, action: 'delete', count: selectedIds.length })
  }

  const handleBulkApply = () => {
    setConfirmDialog({ open: true, action: 'apply', count: selectedIds.length })
  }

  const confirmBulkAction = async () => {
    try {
      if (confirmDialog.action === 'delete') {
        await bulkDeleteMutation.mutateAsync(selectedIds)
      } else if (confirmDialog.action === 'apply') {
        await bulkApplyMutation.mutateAsync(selectedIds)
      }
      setSelectedIds([])
    } catch (error) {
      console.error('Error performing bulk action:', error)
    } finally {
      setConfirmDialog({ open: false, action: null, count: 0 })
    }
  }

  const cancelBulkAction = () => {
    setConfirmDialog({ open: false, action: null, count: 0 })
  }

  const getStatusColor = (status: UpdateStatus) => {
    switch (status) {
      case 'PENDING':
        return 'warning'
      case 'APPLIED':
        return 'success'
      case 'FAILED':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusLabel = (status: UpdateStatus) => {
    switch (status) {
      case 'PENDING':
        return 'En attente'
      case 'APPLIED':
        return 'Appliquée'
      case 'FAILED':
        return 'Échec'
      default:
        return status
    }
  }

  const getProposalTypeLabel = (type: string) => {
    switch (type) {
      case 'NEW_EVENT':
        return 'Nouvel événement'
      case 'EVENT_UPDATE':
        return 'Mise à jour événement'
      case 'EDITION_UPDATE':
        return 'Mise à jour édition'
      case 'RACE_UPDATE':
        return 'Mise à jour course'
      default:
        return type
    }
  }

  const getUpdatePrimaryText = (update: DataUpdate) => {
    const { context, proposal } = update

    // Pour EVENT_UPDATE, utiliser le nom enrichi de la proposition
    if (proposal.type === 'EVENT_UPDATE' && proposal.eventName) {
      const parts = [proposal.eventName]
      if (proposal.eventCity) {
        parts.push(proposal.eventCity)
      }
      return parts.join(' - ')
    }

    // Pour les autres types, utiliser le context
    if (!context?.eventName) {
      return 'Mise à jour'
    }

    const parts = [context.eventName]

    if (context.editionYear) {
      parts.push(context.editionYear)
    }

    return parts.join(' - ')
  }

  const getUpdateSecondaryText = (update: DataUpdate) => {
    return getProposalTypeLabel(update.proposal.type)
  }

  // ✅ Extraire les données AVANT les returns conditionnels (Rules of Hooks)
  const updates = updatesData?.data || []
  const totalCount = updatesData?.meta?.total || 0
  const numSelected = selectedIds.length
  const numPendingSelected = selectedIds.filter(id => {
    const update = updates.find((u: DataUpdate) => u.id === id)
    return update?.status === 'PENDING'
  }).length

  // ✅ Groupement des applications par proposalIds (AVANT les returns conditionnels)
  const groupedUpdates = useMemo(() => {
    const groups = new Map<string, any[]>()

    updates.forEach((app: any) => {
      const key = (app.proposalIds || [app.proposalId]).sort().join('-')
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key)!.push(app)
    })

    return Array.from(groups.values()).map(apps => ({
      id: `group-${(apps[0].proposalIds || [apps[0].proposalId]).join('-')}`,
      applications: apps,
      proposalIds: apps[0].proposalIds || [apps[0].proposalId],
      eventName: apps[0].context?.eventName || apps[0].proposal?.eventName || 'Mise à jour',
      editionYear: apps[0].context?.editionYear,
      blocks: apps.map(a => a.blockType).filter(Boolean),
      status: (apps.some(a => a.status === 'PENDING') ? 'PENDING' :
              apps.every(a => a.status === 'APPLIED') ? 'APPLIED' : 'FAILED') as UpdateStatus
    }))
  }, [updates])

  // Returns conditionnels APRÈS tous les hooks
  if (isLoading) return <LinearProgress />

  if (error) {
    const isNotFound = (error as any)?.response?.status === 404
    return (
      <Card>
        <CardContent>
          {isNotFound ? (
            <Alert severity="info">
              <Typography variant="h6" gutterBottom>Fonctionnalité en cours de développement</Typography>
              <Typography variant="body2">
                L'API des mises à jour n'est pas encore implémentée dans le backend.
                Cette fonctionnalité sera disponible prochainement.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="error">Erreur lors du chargement des mises à jour</Alert>
          )}
        </CardContent>
      </Card>
    )
  }

  // ✅ Helper pour labels des blocs
  const blockLabels: Record<string, string> = {
    event: 'Événement',
    edition: 'Édition',
    organizer: 'Organisateur',
    races: 'Courses'
  }

  // ✅ Handler pour toggle accordions
  const handleToggleGroup = (groupKey: string, event: React.MouseEvent) => {
    event.stopPropagation()
    setExpandedGroups(prev => {
      const newExpanded = new Set(prev)
      if (newExpanded.has(groupKey)) {
        newExpanded.delete(groupKey)
      } else {
        newExpanded.add(groupKey)
      }
      return newExpanded
    })
  }

  return (
    <Box>
      {/* Titre + View Mode Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Mises à jour
        </Typography>

        {/* View Mode Toggle */}
        <Box sx={{ display: 'flex', border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Button
            size="small"
            startIcon={<GroupIcon />}
            onClick={() => setViewMode('grouped')}
            variant={viewMode === 'grouped' ? 'contained' : 'text'}
            sx={{ borderRadius: 0 }}
          >
            Groupé
          </Button>
          <Button
            size="small"
            startIcon={<ViewListIcon />}
            onClick={() => setViewMode('table')}
            variant={viewMode === 'table' ? 'contained' : 'text'}
            sx={{ borderRadius: 0 }}
          >
            Tableau
          </Button>
        </Box>
      </Box>

      {/* Filtres */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="ID proposition, agent..."
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                select
                label="Statut"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as UpdateStatus | '')}
                size="small"
              >
                <MenuItem value="">Tous les statuts</MenuItem>
                <MenuItem value="PENDING">En attente</MenuItem>
                <MenuItem value="APPLIED">Appliquées</MenuItem>
                <MenuItem value="FAILED">Échecs</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Actions bulk */}
      {numSelected > 0 && (
        <Card sx={{ mb: 2, bgcolor: 'primary.50', borderColor: 'primary.main', borderWidth: 1, borderStyle: 'solid' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {numSelected} mise(s) à jour sélectionnée(s)
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {numPendingSelected > 0 && (
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<ApplyIcon />}
                    onClick={handleBulkApply}
                    disabled={bulkApplyMutation.isPending || bulkDeleteMutation.isPending}
                  >
                    Appliquer ({numPendingSelected})
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={handleBulkDelete}
                  disabled={bulkApplyMutation.isPending || bulkDeleteMutation.isPending}
                >
                  Supprimer ({numSelected})
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Rendu conditionnel : Vue Groupée OU Vue Tableau */}
      {viewMode === 'grouped' ? (
        /* ========== VUE GROUPÉE : ACCORDIONS ========== */
        <Box>
          {groupedUpdates.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  Aucune mise à jour trouvée
                </Typography>
              </CardContent>
            </Card>
          ) : (
            groupedUpdates.map(group => {
              const isExpanded = expandedGroups.has(group.id)
              const allAppsIds = group.applications.map((a: any) => a.id)
              const hasSelection = allAppsIds.some(id => selectedIds.includes(id))

              return (
                <Accordion
                  key={group.id}
                  expanded={isExpanded}
                  onChange={(e) => handleToggleGroup(group.id, e as any)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer'
                    }}
                    onClick={(e) => {
                      // Si clic sur la zone principale (pas expand icon), naviguer vers détail
                      const target = e.target as HTMLElement
                      if (!target.closest('.MuiAccordionSummary-expandIconWrapper')) {
                        e.stopPropagation()
                        // Utiliser l'ID de la première application du groupe
                        navigate(`/updates/group/${group.applications[0].id}`)
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      {/* Checkbox groupe */}
                      <Checkbox
                        checked={hasSelection}
                        indeterminate={hasSelection && !allAppsIds.every(id => selectedIds.includes(id))}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (hasSelection) {
                            setSelectedIds(prev => prev.filter(id => !allAppsIds.includes(id)))
                          } else {
                            setSelectedIds(prev => [...prev, ...allAppsIds])
                          }
                        }}
                      />

                      {/* Nom événement */}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {group.eventName}
                          {group.editionYear && (
                            <Typography component="span" variant="body2" sx={{ ml: 1, color: 'text.secondary' }}>
                              ({group.editionYear})
                            </Typography>
                          )}
                        </Typography>

                        {/* Chips des blocs */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                          {group.blocks.map((block: string) => (
                            <Chip
                              key={block}
                              label={blockLabels[block] || block}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                          {group.blocks.length === 0 && (
                            <Chip label="Tous les blocs" size="small" variant="outlined" />
                          )}
                        </Box>
                      </Box>

                      {/* Statut global du groupe */}
                      <Chip
                        label={getStatusLabel(group.status)}
                        color={getStatusColor(group.status) as any}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>

                  <AccordionDetails sx={{ bgcolor: 'action.hover' }}>
                    {/* Liste des applications individuelles */}
                    <List disablePadding>
                      {group.applications.map((app: any, index: number) => (
                        <React.Fragment key={app.id}>
                          {index > 0 && <Divider />}
                          <ListItem
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              py: 2,
                              bgcolor: 'background.paper'
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              {/* Titre application */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                {app.blockType && (
                                  <Chip
                                    label={blockLabels[app.blockType] || app.blockType}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  label={getStatusLabel(app.status)}
                                  color={getStatusColor(app.status) as any}
                                  size="small"
                                />
                              </Box>

                              {/* Dates */}
                              <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                                <Box>
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    Programmée
                                  </Typography>
                                  <Typography variant="body2">
                                    {app.scheduledAt
                                      ? format(new Date(app.scheduledAt), 'dd/MM/yyyy HH:mm', { locale: fr })
                                      : '-'
                                    }
                                  </Typography>
                                </Box>
                                {app.appliedAt && (
                                  <Box>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      Appliquée
                                    </Typography>
                                    <Typography variant="body2">
                                      {format(new Date(app.appliedAt), 'dd/MM/yyyy HH:mm', { locale: fr })}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>

                            {/* Actions */}
                            <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                              <Tooltip title="Voir les détails">
                                <IconButton
                                  size="small"
                                  onClick={() => navigate(`/updates/${app.id}`)}
                                >
                                  <ViewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>

                              {app.status === 'PENDING' && (
                                <Tooltip title="Appliquer maintenant">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleApplyUpdate(app.id)}
                                    disabled={applyUpdateMutation.isPending}
                                  >
                                    <ApplyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {app.status === 'APPLIED' && (
                                <Tooltip title="Supprimer">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteUpdate(app.id)}
                                    disabled={deleteUpdateMutation.isPending}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </ListItem>
                        </React.Fragment>
                      ))}
                    </List>

                    {/* Actions groupées en bas de l'accordion */}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => navigate(`/updates/group/${group.proposalIds.join('-')}`)}
                      >
                        Voir les détails du groupe
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => navigate(`/proposals/group/${group.proposalIds.join('-')}`)}
                      >
                        Voir la proposition
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )
            })
          )}
        </Box>
      ) : (
        /* ========== VUE TABLEAU : TABLE ========== */
        <Card>
          <CardContent sx={{ p: 0 }}>
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={numSelected > 0 && numSelected < updates.length}
                        checked={updates.length > 0 && numSelected === updates.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell>Mise à jour</TableCell>
                    <TableCell>Agent</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Statut</TableCell>
                    <TableCell>Programmée le</TableCell>
                    <TableCell>Appliquée le</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {updates.map((update: DataUpdate) => {
                    const isSelected = selectedIds.includes(update.id)
                    return (
                    <TableRow key={update.id} hover selected={isSelected}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleSelectOne(update.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box component={Link} to={`/proposals/${update.proposalId}`} sx={{ textDecoration: 'none', display: 'block' }}>
                          <Typography
                            variant="body1"
                            sx={{ color: 'primary.main', fontWeight: 600, mb: 0.5 }}
                          >
                            {getUpdatePrimaryText(update)}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {getUpdateSecondaryText(update)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{update.proposal.agent.name}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {getProposalTypeLabel(update.proposal.type)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={getStatusLabel(update.status)}
                          color={getStatusColor(update.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {update.scheduledAt
                            ? format(new Date(update.scheduledAt), 'dd/MM/yyyy HH:mm', { locale: fr })
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {update.appliedAt
                            ? format(new Date(update.appliedAt), 'dd/MM/yyyy HH:mm', { locale: fr })
                            : '-'
                          }
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <Tooltip title="Voir les détails">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/updates/${update.id}`)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {update.status === 'PENDING' && (
                            <Tooltip title="Appliquer maintenant">
                              <span>
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleApplyUpdate(update.id)}
                                  disabled={applyUpdateMutation.isPending}
                                >
                                  <ApplyIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}

                          {update.status === 'APPLIED' && (
                            <Tooltip title="Supprimer">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteUpdate(update.id)}
                                  disabled={deleteUpdateMutation.isPending}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                    )
                  })}
                  {updates.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                          Aucune mise à jour trouvée
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lignes par page:"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
            />
          </CardContent>
        </Card>
      )}

      {/* Dialog de confirmation */}
      <Dialog open={confirmDialog.open} onClose={cancelBulkAction}>
        <DialogTitle>
          {confirmDialog.action === 'delete' ? 'Confirmer la suppression' : 'Confirmer l\'application'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirmDialog.action === 'delete'
              ? `Êtes-vous sûr de vouloir supprimer ${confirmDialog.count} mise(s) à jour ? Cette action est irréversible.`
              : `Êtes-vous sûr de vouloir appliquer ${numPendingSelected} mise(s) à jour en attente ?`
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelBulkAction}>Annuler</Button>
          <Button
            onClick={confirmBulkAction}
            color={confirmDialog.action === 'delete' ? 'error' : 'primary'}
            variant="contained"
            disabled={bulkApplyMutation.isPending || bulkDeleteMutation.isPending}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default UpdateList
